import { alpha, createTheme, responsiveFontSizes } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";

/* ===========================
   PALETTE AUGMENTATION
=========================== */
declare module "@mui/material/styles" {
  interface Palette {
    customBackground: { box: string };
    highlightedRow: { main: string };
    link: { main: string };
  }
  interface PaletteOptions {
    customBackground?: { box: string };
    highlightedRow?: { main: string };
    link?: { main: string };
  }
}

/* ===========================
   TYPES
=========================== */
export type ThemeColors = {
  primary: { main: string; light?: string; dark?: string };
  secondary: { main: string };
  background: {
    default: string;
    paper: string;
    custom: string;
    body: string;
  };
  text: {
    primary: string;
    secondary: string;
  };
  link: string;
  highlightedRow: string;
  borders: {
    appBar: string;
  };
  dataGrid: {
    header: string;
  };
};

/* ===========================
   COMPONENT OVERRIDES (TEK)
   "Structured Console": crisp bordered surfaces, rectangular nav with a
   left accent bar on the active item, no soft floating shadows/pills.
   Hierarchy comes from clear 1px borders + a distinct canvas tone, not
   from blending everything into one tinted surface.
=========================== */
const getSharedComponents = (mode: "light" | "dark", c: ThemeColors) => {
  const divider =
    mode === "dark" ? "rgba(255, 255, 255, 0.09)" : "rgba(15, 17, 21, 0.09)";
  const hoverTint = alpha(c.text.primary, mode === "dark" ? 0.07 : 0.045);
  const selectedTint = alpha(c.primary.main, mode === "dark" ? 0.18 : 0.1);
  const selectedHoverTint = alpha(
    c.primary.main,
    mode === "dark" ? 0.24 : 0.15,
  );

  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: c.background.body,
          color: c.text.primary,
          scrollbarWidth: "thin",
          scrollbarColor: `${alpha(c.primary.main, 0.35)} transparent`,
        },
        "*::-webkit-scrollbar": {
          width: 6,
          height: 6,
        },
        "*::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "*::-webkit-scrollbar-thumb": {
          background: alpha(c.primary.main, 0.35),
          borderRadius: 999,
        },
        "*::-webkit-scrollbar-thumb:hover": {
          background: c.primary.main,
        },
        "main.MuiBox-root": {
          backgroundColor: c.background.custom,
        },
        a: {
          color: c.link,
          textDecoration: "none",
          fontWeight: 500,
        },

        // Refine's ThemedSiderV2 exposes no sx/className, so the nav sidebar's
        // menu items are restyled here instead. Scoped to the sidebar's own
        // <nav> landmark (the only one in the app) so it can't leak onto
        // unrelated List/ListItemButton usage elsewhere (menus, other lists).
        "nav .MuiDrawer-docked .MuiDrawer-paper": {
          borderRight: `1px solid ${divider}`,
        },
        "nav .MuiListItemButton-root": {
          borderRadius: 8,
          marginLeft: 8,
          marginRight: 8,
          marginBottom: 2,
          borderLeft: "3px solid transparent",
          transition:
            "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
          "&:hover": {
            backgroundColor: hoverTint,
          },
          "&.Mui-selected": {
            backgroundColor: selectedTint,
            borderLeft: `3px solid ${c.primary.main}`,
            "&:hover": {
              backgroundColor: selectedHoverTint,
            },
          },
        },
        "nav .MuiListItemIcon-root": {
          transition: "transform 0.15s ease",
        },
        // ThemedSiderV2's item labels use noWrap Typography, which
        // ellipsis-truncates longer menu titles. Let them wrap onto a
        // second line instead of clipping the text — but only while
        // expanded; when collapsed (see SyncrosaleLayout's Sider, which
        // tags the list with .sider-collapsed) the icon-only rail has no
        // room for labels at all, so they're hidden outright instead of
        // overflowing the narrow width.
        "nav .MuiListItemText-root .MuiTypography-root": {
          whiteSpace: "normal",
          overflow: "visible",
          textOverflow: "clip",
        },
        "nav .sider-collapsed .MuiListItemText-root": {
          display: "none",
        },
        "nav .MuiListItemButton-root:hover .MuiListItemIcon-root": {
          transform: "scale(1.08)",
        },

        // ThemedSiderV2's built-in collapse toggle only ever collapses (it
        // has no way to expand again once collapsed), so it's hidden in
        // favor of the symmetric toggle Header.tsx renders at the far left
        // of the top navbar instead.
        "nav .MuiDrawer-paper > .MuiPaper-root > .MuiIconButton-root": {
          display: "none",
        },
      },
    },

    MuiAppBar: {
      defaultProps: {
        color: "default" as const,
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: c.background.paper,
          height: "64px",
          color: c.text.primary,
          borderBottom: `1px solid ${divider}`,
        },
      },
    },

    // Every non-outlined Paper (page sections, dialogs, menus, popovers)
    // gets a crisp thin border instead of a floating shadow. `outlined`
    // Paper (OrderCard/ProductCard) is left untouched — they already define
    // their own border/hover treatment.
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ ownerState }: { ownerState: { variant?: string } }) => ({
          ...(ownerState.variant !== "outlined" && {
            backgroundImage: "none",
            border: `1px solid ${divider}`,
            borderRadius: 10,
            boxShadow: "none",
          }),
        }),
      },
    },

    MuiButton: {
      styleOverrides: {
        root: ({
          ownerState,
        }: {
          ownerState: { variant?: string; color?: string };
        }) => ({
          borderRadius: 8,
          textTransform: "none" as const,
          fontWeight: 600,
          ...(ownerState.variant === "contained" &&
            ownerState.color === "primary" && {
              boxShadow: "none",
            }),
        }),
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },

    MuiTypography: {
      defaultProps: {
        variant: "body2" as const,
      },
    },

    // Segmented tab strip instead of an underline indicator: each tab is a
    // discrete rectangular button, the active one gets its own paper
    // surface + border so it reads as "selected", not just "underlined".
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
          padding: 4,
          borderRadius: 8,
          border: `1px solid ${divider}`,
          backgroundColor: c.background.custom,
        },
        indicator: {
          display: "none",
        },
        flexContainer: {
          gap: 4,
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none" as const,
          fontWeight: 600,
          minHeight: 32,
          minWidth: 0,
          padding: "6px 14px",
          borderRadius: 6,
          color: c.text.secondary,
          border: "1px solid transparent",
          transition:
            "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
          "&:hover": {
            backgroundColor: hoverTint,
          },
          "&.Mui-selected": {
            backgroundColor: c.background.paper,
            borderColor: divider,
            color: c.primary.main,
          },
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          minHeight: 500,
          backgroundColor: c.background.paper,
          border: `1px solid ${divider}`,
          borderRadius: 10,
        },
        columnHeader: {
          backgroundColor: c.dataGrid.header,
          fontWeight: 600,
        },
        footerContainer: {
          backgroundColor: c.dataGrid.header,
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
  };
};

/* ===========================
   THEME FACTORY (TEK)
=========================== */
export const createAppTheme = (mode: "light" | "dark", colors: ThemeColors) =>
  responsiveFontSizes(
    createTheme({
      shape: {
        borderRadius: 8,
      },
      palette: {
        mode,
        primary: {
          ...colors.primary,
          contrastText: "#ffffff",
        },
        secondary: colors.secondary,
        background: {
          default: colors.background.default,
          paper: colors.background.paper,
        },
        customBackground: {
          box: colors.background.custom,
        },
        highlightedRow: {
          main: colors.highlightedRow,
        },
        text: colors.text,
        link: {
          main: colors.link,
        },
      },
      components: getSharedComponents(mode, colors),
    }),
  );
